import { useEffect } from "react";
import { IMAGES } from "../images/images"
import { Phone, Truck, Package2, MapPin, Clock, Shield, CheckCircle } from "lucide-react";
import { trackPhoneConversion } from "../utils/tracking";

const PHONE = "+359876995651";
const PHONE_DISPLAY = "Обадете се";

const vehicles = [
  {
    icon: Truck,
    title: "Камион с падащ борд",
    specs: ["Голям товарен капацитет", "Падащ борд за тежки товари", "Подходящ за мебели и уреди", "Превоз на всякаква тежест"],
    desc: "Нашите камиони са оборудвани с падащ борд, което позволява безопасно натоварване и разтоварване на тежки и габаритни предмети.",
  },
  {
    icon: Package2,
    title: "Микробус",
    specs: ["По-нисък разход за кратко разстояние", "Идеален за градска среда", "Бърз достъп в тесни улици", "Икономично решение"],
    desc: "Микробусовете ни са перфектни за по-малки обеми товари и превоз в градска среда. По-малкият размер позволява лесен достъп до сгради с ограничен паркинг.",
  },
];

const benefits = [
  { icon: Shield, text: "Пълна отговорност за вашите вещи по време на транспорта" },
  { icon: MapPin, text: "Покриваме цяла София и прилежащата София-област" },
  { icon: Clock, text: "Точност и спазване на уговореното работно време" },
  { icon: CheckCircle, text: "Опитни шофьори с познание на София и провинцията" },
  { icon: CheckCircle, text: "Фиксирани цени — без изненади и скрити такси" },
  { icon: CheckCircle, text: "Безплатна консултация за вида и размера на транспортното средство" },
];

export default function TransportniUslugiPage() {
  useEffect(() => {
    document.title = "Транспортни услуги София | Хамали преместване с бус — Хамали София";
    const setMeta = (name, content, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    setMeta("description", "Транспортни услуги в София — бусове и камиони с падащ борд за пренасяне и преместване. Покриваме цяла София и областта. Обадете се: +359 876 995 651");
    setMeta("og:title", "Транспортни услуги София | Хамали преместване с бус — Хамали София", true);
    setMeta("og:description", "Транспорт с микробус и камион в цяла София. Падащ борд за тежки товари. Фиксирани цени, без изненади. Безплатна консултация.", true);
    setMeta("og:url", "https://hamalski-sofia.com/transportni-uslugi", true);
    setCanonical("https://hamalski-sofia.com/transportni-uslugi");
  }, []);

  return (
    <div style={{ backgroundColor: "#FFFFFF", paddingTop: "68px" }}>

      {/* HERO — dark */}
      <section data-testid="transport-hero" style={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMAGES.TransportHero})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="hero-overlay" />
        <div style={{ position: "relative", zIndex: 2, maxWidth: "1280px", margin: "0 auto", padding: "4rem 1.25rem", width: "100%" }}>
          <p style={{ color: "#2dcf0c", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1rem" }}>Транспортни и хамалски услуги — София</p>
          <h1 data-testid="transport-h1" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "#FFFFFF", textTransform: "uppercase", maxWidth: "700px", marginBottom: "1.5rem" }}>
            Транспортни и Хамалски Услуги —{" "}
            <span style={{ color: "#2dcf0c" }}>Преместване с Бус</span>
          </h1>
          <p style={{ color: "#d4d4d8", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: "560px", marginBottom: "2rem" }}>
            Разполагаме с надеждни бусове и камиони за сигурен превоз на мебели, багаж и оборудване в София и цялата страна.
          </p>
          <a href={`tel:${PHONE}`} data-testid="transport-call-btn" className="btn-call" style={{ padding: "1rem 2.25rem", fontSize: "1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
            <Phone size={20} />
            <span>{PHONE_DISPLAY}</span>
          </a>
        </div>
      </section>

      {/* DESCRIPTION — white */}
      <section style={{ padding: "4rem 1.25rem", maxWidth: "900px", margin: "0 auto" }}>
        <p style={{ fontSize: "1.075rem", color: "#374151", lineHeight: 1.85, borderLeft: "3px solid #2dcf0c", paddingLeft: "1.5rem" }}>
          <strong style={{ color: "#0A0A0A" }}>Хамали София</strong> предоставя бързи и надеждни транспортни услуги до всяка точка на София и страната. Нашият автопарк включва микробуси и камиони с падащ борд, подходящи за превоз на всякакви товари. Без значение от обема и теглото — имаме правилния транспорт за вашия случай.
        </p>
      </section>

      {/* VEHICLES — light grey */}
      <section data-testid="transport-vehicles" style={{ backgroundColor: "#F8F8FA", borderTop: "1px solid rgba(0,0,0,0.06)", padding: "4rem 1.25rem" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", textAlign: "center", marginBottom: "3rem" }}>
            Нашият <span style={{ color: "#2dcf0c" }}>Автопарк</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.5rem" }}>
            {vehicles.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} data-testid={`vehicle-card-${v.title.split(' ')[0].toLowerCase()}`}
                  style={{ padding: "2.25rem", backgroundColor: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "8px", boxShadow: "0 2px 16px rgba(0,0,0,0.05)", transition: "box-shadow 0.25s ease, border-color 0.25s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(45,207,12,0.12)"; e.currentTarget.style.borderColor = "rgba(45,207,12,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.05)"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; }}>
                  <div style={{ width: 56, height: 56, backgroundColor: "rgba(45,207,12,0.08)", border: "1px solid rgba(45,207,12,0.2)", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem" }}>
                    <Icon size={26} color="#2dcf0c" />
                  </div>
                  <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1.1rem", color: "#0A0A0A", textTransform: "uppercase", marginBottom: "0.875rem" }}>{v.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: "0.875rem", lineHeight: 1.7, marginBottom: "1.25rem" }}>{v.desc}</p>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {v.specs.map((spec) => (
                      <li key={spec} style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#374151", fontSize: "0.85rem" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#2dcf0c", flexShrink: 0 }} />
                        {spec}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS — white */}
      <section data-testid="transport-benefits" style={{ padding: "4rem 1.25rem", maxWidth: "900px", margin: "0 auto" }}>
        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", marginBottom: "2.5rem" }}>
          Нашите <span style={{ color: "#2dcf0c" }}>Предимства</span>
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="feature-item" style={{ display: "flex", alignItems: "flex-start", gap: "0.875rem", padding: "1rem" }}>
                <Icon size={18} color="#2dcf0c" style={{ marginTop: "2px", flexShrink: 0 }} />
                <p style={{ color: "#374151", fontSize: "0.9rem", lineHeight: 1.65 }}>{b.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="transport-cta" style={{ padding: "4rem 1.25rem", textAlign: "center", backgroundColor: "#F0FDF4", borderTop: "1px solid rgba(45,207,12,0.2)" }}>
        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", marginBottom: "1rem" }}>
          Нужен ви е транспорт? <span style={{ color: "#2dcf0c" }}>Позвънете сега!</span>
        </h2>
        <p style={{ color: "#6B7280", marginBottom: "2rem", fontSize: "1rem" }}>Бързи и надеждни транспортни услуги в София и София-областта. Безплатна консултация!</p>
        <a href={`tel:${PHONE}`} data-testid="transport-cta-btn" className="btn-call" style={{ padding: "1.1rem 2.5rem", fontSize: "1.1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
          <Phone size={22} />
          <span>{PHONE_DISPLAY}</span>
        </a>
      </section>
    </div>
  );
}
